import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { Geography } from "../../shared/models/geography.model";
import { UserGeography } from "../../shared/models/user-geography.model";
import { ISelectedGeography } from "../../shared/interfaces/selected-geography.interface";
import { IGeographyNode } from "../../shared/interfaces/geography-node.interface";
import { GeographyMenuOption } from "../../shared/models/geography-menu-option.model";
import { TreeComponent } from "@circlon/angular-tree-component";
import { COUNTRY_TYPE_DATA, PREDEFINED_MENU_OPTIONS } from "../../shared/constants/country-type.data";
import { COUNTRIES } from "../../shared/constants/countries.data";
import { FavouriteGeography } from "../../shared/models/favourite-geography.response.model";
import { IFavouriteGeographyNode } from "../../shared/models/favourite-geography-node.model";

@Injectable({
    providedIn: 'root'
})
export class TreeNodeService {

    public mapGeographiesToTreeNodes(
        geographiesWithParents: ReadonlyArray<Geography>,
        userGeographies: ReadonlyArray<UserGeography>,
        selectedGeographies: Array<ISelectedGeography>
    ): Array<IGeographyNode> {
        const filteredGeographies = this.getGeographiesMapToParent(geographiesWithParents, userGeographies);
        const geographyNodes = filteredGeographies.map(
            (geography: Geography) =>
                ({
                    code: geography.emiCode,
                    geographyId: geography.geographyId,
                    parent: geography.parent ? geography.parent.emiCode : undefined,
                    name: geography.name,
                    typeId: geography.typeId,
                    tierId: geography.tierId,
                    isSelected: selectedGeographies.map(sg => sg.emiCode).includes(geography.emiCode),
                    isDisabled: !userGeographies.some((userGeography: UserGeography) => userGeography.geographyCode === geography.emiCode),
                    children: [],
                    menuOptions: this.buildGeographyMenuOptions(
                        geography.emiCode,
                        this.getAllChildrenForGeography(geography.emiCode, filteredGeographies),
                        userGeographies
                    ),
                    hierarchy: this.buildGeographyHierarchy(geography.emiCode, filteredGeographies)
                } as IGeographyNode)
        );

        return this.mapChildGeographiesToParents(
            geographyNodes.filter((geographyNode: IGeographyNode) => !geographyNode.parent),
            geographyNodes,
            filteredGeographies,
            userGeographies
        );
    }

    private getGeographiesMapToParent(
        geographiesWithParents: ReadonlyArray<Geography>,
        userGeographies: ReadonlyArray<UserGeography>
    ): Array<Geography> {
        const userGeographiesMappedToParent = this.mapUserGeographiesToParent(
            geographiesWithParents.filter(g => userGeographies.some(ug => ug.geographyCode === g.emiCode)),
            geographiesWithParents
        );
        return geographiesWithParents.filter(g => userGeographiesMappedToParent.some(c => c.emiCode === g.emiCode));
    }

    public flattenGeographySelections(geographyNodes: Array<IGeographyNode>): Array<ISelectedGeography> {
        if (!geographyNodes) {
            return [];
        }

        return geographyNodes
            .reduce<Array<ISelectedGeography>>(
                (result, item) => [
                    ...result,
                    {
                        emiCode: item.code,
                        geographyId: item.geographyId,
                        name: item.name,
                        isSelected: item.isSelected,
                        typeId: item.typeId,
                        hierarchy: item.hierarchy,
                        lastModifiedDate:''
                    } as ISelectedGeography,
                    ...this.flattenGeographySelections(item.children)
                ],
                []
            )
            .filter(s => s.isSelected);
    }

    public deselectGeographyTreeNode(selectedGeographyTreeNodes: Array<IGeographyNode>, geographyCode: string): Array<IGeographyNode> {
        if (!selectedGeographyTreeNodes) {
            return [];
        }

        return selectedGeographyTreeNodes.map((geographyTreeNode: IGeographyNode) =>
            geographyCode === geographyTreeNode.code
                ? {
                      ...geographyTreeNode,
                      isSelected: false
                  }
                : {
                      ...geographyTreeNode,
                      children: this.deselectGeographyTreeNode(geographyTreeNode.children, geographyCode)
                  }
        );
    }

    public deselectAllGeographyTreeNodes(selectedGeographyTreeNodes: Array<IGeographyNode>): Array<IGeographyNode> {
        if (!selectedGeographyTreeNodes) {
            return [];
        }

        return selectedGeographyTreeNodes.map((geographyTreeNode: IGeographyNode) => ({
            ...geographyTreeNode,
            isSelected: false,
            children: this.deselectAllGeographyTreeNodes(geographyTreeNode.children)
        }));
    }

   
    private buildGeographyMenuOptions(
        code: string,
        allChildren: Array<Geography>,
        userGeographies: ReadonlyArray<UserGeography>
    ): Array<GeographyMenuOption> {
        const menuTitle = PREDEFINED_MENU_OPTIONS.menuTitle;
        const uniqueTierCountryId = new Set<{ tierId: number; typeId: number }>();
        const shouldDisplayCountryMenu = (children: Array<Geography>) => {
            const countries = Array.from(COUNTRIES);
            let shouldDisplay = false;
            for (const child of children) {
                if (countries.some(country => country.countryCode === child.emiCode)) {
                    shouldDisplay = true;
                    break;
                }
            }
            return shouldDisplay;
        };

        for (const child of allChildren.filter((childGeography: Geography) =>
            userGeographies.some((userGeography: UserGeography) => userGeography.geographyCode === childGeography.emiCode)
        )) {
            uniqueTierCountryId.add({ tierId: child.tierId, typeId: child.typeId });
        }

        const menuData = COUNTRY_TYPE_DATA.filter((country: { countryTypeId: number; countryTypeName: string; countryTierId: number }) =>
            Array.from(uniqueTierCountryId).some(
                (uniqueId: { tierId: number; typeId: number }) =>
                    country.countryTierId === uniqueId.tierId && country.countryTypeId === uniqueId.typeId
            )
        );
        if (shouldDisplayCountryMenu(allChildren)) {
            menuData.unshift(PREDEFINED_MENU_OPTIONS.countriesTypeMenu);
        }
        const mappedMenuData = menuData.map(item => ({
            nodeCode: code,
            tierId: item.countryTierId,
            typeId: item.countryTypeId,
            tierName: item.countryTypeName,
            countryName: item.countryTypeName,
            isSelected: false,
            title: `${menuTitle} ${item.countryTypeName}`
        }));

        if (
            PREDEFINED_MENU_OPTIONS.worldCode === code &&
            userGeographies.some((userGeography: UserGeography) => userGeography.geographyCode === code)
        ) {
            mappedMenuData.unshift(PREDEFINED_MENU_OPTIONS.worldMenu);
        }
        return mappedMenuData;
    }

    private getAllChildrenForGeography(geographyCode: string, geographies: ReadonlyArray<Geography>): Array<Geography> {
        let children = geographies.filter(product => product.parent?.emiCode === geographyCode);
        let allChildren: Array<Geography> = [];
        while (children.length !== 0) {
            allChildren = [...allChildren, ...children];
            children = children
                .map(child => geographies.filter(product => product.parent?.emiCode === child.emiCode))
                .reduce((acc, item) => [...acc, ...item], []);
        }
        return allChildren;
    }

    private mapChildGeographiesToParents(
        parents: Array<IGeographyNode>,
        allCountries: Array<IGeographyNode>,
        geographies: ReadonlyArray<Geography>,
        userGeographies: ReadonlyArray<UserGeography>
    ): Array<IGeographyNode> {
        return parents.map(
            ({ code, geographyId,name, parent, isSelected, isDisabled, typeId, tierId }) =>
                ({
                    code,
                    geographyId,
                    name,
                    parent,
                    typeId,
                    tierId,
                    isSelected,
                    isDisabled,
                    hierarchy: this.buildGeographyHierarchy(code, geographies),
                    children: this.mapChildGeographiesToParents(
                        allCountries.filter(country => country.parent === code),
                        allCountries,
                        geographies,
                        userGeographies
                    ),
                    menuOptions: this.buildGeographyMenuOptions(code, this.getAllChildrenForGeography(code, geographies), userGeographies)
                } as IGeographyNode)
        );
    }

    private buildGeographyHierarchy(geographyCode: string, geographies: ReadonlyArray<Geography>): string {
        const geographiesAndParents = geographies.reduce((acc: { [key: string]: string | undefined }, item) => {
            acc[item['name']] = item['parent']?.name;
            return acc;
        }, {} as { [key: string]: string | undefined });

        let hierarchy = '';
        const foundGeography = geographies.find(p => p.emiCode === geographyCode);
        let parentName = foundGeography?.parent?.name;

        while (parentName) {
            hierarchy = `${parentName}/ ${hierarchy}`;
            parentName = geographiesAndParents[parentName];
        }

        return hierarchy.trim();
    }

    public expandTreeNodes(nodeIds: Array<number>, tree: TreeComponent): void {
        if (nodeIds?.length > 0) {
          nodeIds.forEach(item => {
            const nodeToBeExpanded = tree.treeModel.getNodeById(item);
            if (nodeToBeExpanded && nodeToBeExpanded.children?.length > 0) {
              nodeToBeExpanded.expand();
            }
          });
        }
    }


    private mapUserGeographiesToParent(children: ReadonlyArray<Geography>, geographies: ReadonlyArray<Geography>): Array<Geography> {
        let filteredGeographies = geographies.filter(g => children?.some(n => n.parent?.emiCode === g.emiCode));
        children.forEach(child => {
            if (!filteredGeographies.includes(child)) {
                filteredGeographies.push(child);
            }
        });

        if (filteredGeographies.length > 0 && !filteredGeographies.every(p => children.some(c => c.emiCode === p.emiCode))) {
            filteredGeographies = this.mapUserGeographiesToParent(filteredGeographies, geographies);
        }

        return filteredGeographies;
    }

    public getExpandedNodeIds(tree: TreeComponent): Array<number> {
        const expandedNodeIdsProxy = tree.treeModel.expandedNodeIds;
        return Object.keys(expandedNodeIdsProxy).filter(
          key => expandedNodeIdsProxy[key]
        ).map(Number);
    }

    public expandToNode(nodes: Array<any>, nodeId: number): boolean {
        for (const node of nodes) {
            if (node.id === nodeId) {
                return true;
            }
            if (node.children && node.children.length > 0) {
                if (this.expandToNode(node.children, nodeId)) {
                    node.expanded = true;
                    return true;
                }
            }
        }
        return false;
    }

    public setIsSelectedFalse(data: any): void {
        if (Array.isArray(data)) {
            data.forEach((item) => {
                this.setIsSelectedFalse(item);
            });
        } else if (typeof data === 'object') {
            if (data.isSelected === true) {
                data.isSelected = false;
            }
            if (data.children) {
                this.setIsSelectedFalse(data.children);
            }
        }
    }

    public toggleNode(node: any): void {
        node.expanded = !node.expanded;
    }

    public handleManualNodeSelect(
        node: any,
        selectedItems: Array<any>,
        highlightedNodeId: number | null = null
    ): { selectedItems: Array<any>; highlightedNodeId: number | null } {
        const isSelected = selectedItems.some((item) => item.id === node.id);
        let updatedItems = [...selectedItems];
        let updatedHighlightId = highlightedNodeId;

        if (node.isSelected && !isSelected) {
            updatedItems.push(node);
            updatedHighlightId = node.id;
        } else if (!node.isSelected) {
            updatedItems = updatedItems.filter((item) => item.id !== node.id);
            updatedHighlightId = updatedItems.length > 0 ? updatedItems[updatedItems.length - 1].id : null;
        }

        return {
            selectedItems: updatedItems,
            highlightedNodeId: updatedHighlightId
        };
    }

    public handleGeographyMenuClick(
        menuItem: any,
        selectedNode: any,
        userGeographies: Array<any>,
        dialogTreeData: Array<any>,
        geographyService: any
    ): Array<any> {
        geographyService.arrangeGeographyNodes(menuItem, selectedNode, userGeographies);
        return this.flattenGeographySelections(dialogTreeData);
    }

    public refreshGeographyNodesMenuOptions(
        nodes: Array<any>,
        tree: any,
        userGeographies: Array<any>,
        geographyService: any
    ): void {
        nodes.forEach((node) =>
            geographyService.iterateGeographyNodesToRefresh(node, tree, userGeographies)
        );
    }

    public mapFavouritesToTreeNodes(
        favourites: ReadonlyArray<FavouriteGeography>,
        treeNodes: Array<IGeographyNode>,
        previousFavouriteNodes: Array<IFavouriteGeographyNode> = [],
        highlightedCodes: Set<string> = new Set()
    ): Array<IFavouriteGeographyNode> {
        const rawNodes = favourites
            .map(favourite => {
                const allMemberNodes = favourite.geographyIds
                    .map(id => this.findNodeByGeographyId(treeNodes, id))
                    .filter((node): node is IGeographyNode => !!node);

                const memberNodes = allMemberNodes.filter(node => !node.isDisabled);
                const memberNames = memberNodes.map(node => node.name);

                const hasCustomName = favourite.name.includes(',')
                                        ? false
                                        : favourite.geographyIds.length === 1
                                            ? memberNames.length === 1 && favourite.name.trim() !== memberNames[0].trim()
                                            : true;

                const displayName = hasCustomName
                    ? favourite.name
                    : memberNames.join(', ');

                const previousNode = previousFavouriteNodes.find(p => p.code === favourite.id);

                return {
                    code: favourite.id,
                    geographyId: favourite.geographyIds[0] || '',
                    name: displayName,
                    isSelected: memberNodes.length > 0 && memberNodes.every(node => node.isSelected),
                    isDisabled: memberNodes.length === 0,
                    children: memberNodes,
                    geographyIds: favourite.geographyIds,
                    memberNames,
                    isExpanded: previousNode?.isExpanded ?? false,
                    isFavourite: true,
                    hasCustomName,
                    hierarchy: '',
                    typeId: 0,
                    tierId: 0,
                    menuOptions: []
                } as IFavouriteGeographyNode;
            })
            .filter(fav =>
                fav.memberNames.length > 0 ||
                (fav.hasCustomName && fav.children.length > 0)
            );

        const explicitlyHighlighted = rawNodes.filter(fav =>
            highlightedCodes.has(fav.code) &&
            fav.children.length > 0 &&
            fav.children.every(child => child.isSelected)
        );

        const subsetDisabledByHighlightedSuperset = new Set(
        rawNodes
            .filter((favourite) =>
            favourite.children.length > 0 &&
            explicitlyHighlighted.some((highlightedSuperset) =>
                highlightedSuperset.code !== favourite.code &&
                (this.isStrictSubset(favourite.children, highlightedSuperset.children) ||
                this.isSameSet(favourite.children, highlightedSuperset.children))
            )
            )
            .map((favourite) => favourite.code)
        );

        const suppressedBySuperset = new Set<string>();
        explicitlyHighlighted.forEach(fav => {
            const favNodeIdx = rawNodes.findIndex(f => f.code === fav.code);
            explicitlyHighlighted.forEach(other => {
                if (other.code === fav.code) return;
                const otherNodeIdx = rawNodes.findIndex(f => f.code === other.code);
                const membersContainedInOther = fav.children.every(child =>
                    other.children.some((oc: IGeographyNode) => oc.code === child.code)
                );
                const isStrictSuperset = membersContainedInOther && other.children.length > fav.children.length;
                const isEqualSetAlreadyExists = membersContainedInOther && other.children.length === fav.children.length && otherNodeIdx < favNodeIdx;
                if (isStrictSuperset || isEqualSetAlreadyExists) {
                    suppressedBySuperset.add(fav.code);
                }
            });
        });

        const finalHighlighted = new Set(
            explicitlyHighlighted
                .filter(fav => !suppressedBySuperset.has(fav.code))
                .map(fav => fav.code)
        );

        return rawNodes.map(fav => ({
            ...fav,
            isSelected: finalHighlighted.has(fav.code),
            isDisabled: fav.isDisabled || subsetDisabledByHighlightedSuperset.has(fav.code)
        }));
    }

    public setSelectionByCode(
        nodes: Array<IGeographyNode>,
        codes: ReadonlyArray<string>,
        isSelected: boolean
    ): Array<IGeographyNode> {
        return nodes.map(node => {
            const isMatch = codes.includes(node.code);
            if (node.children?.length) {
                const updatedChildren = this.setSelectionByCode(node.children, codes, isSelected);
                return {
                    ...node,
                    isSelected: isMatch ? isSelected : node.isSelected,
                    children: updatedChildren
                };
            }
            return isMatch ? { ...node, isSelected } : node;
        });
    }

    private findNodeByGeographyId(
        nodes: Array<IGeographyNode>,
        geographyId: string
    ): IGeographyNode | null {
        for (const node of nodes) {
            if (node.geographyId === geographyId) {
                return node;
            }
            if (node.children?.length) {
                const found = this.findNodeByGeographyId(node.children, geographyId);
                if (found) return found;
            }
        }
        return null;
    }

    private isStrictSubset(nodes: Array<IGeographyNode>, otherNodes: Array<IGeographyNode>): boolean {
        return (
        nodes.length > 0 &&
        nodes.length < otherNodes.length &&
        nodes.every((node) => otherNodes.some((otherNode) => otherNode.geographyId === node.geographyId))
        );
    }

    private isSameSet(nodes: Array<IGeographyNode>, otherNodes: Array<IGeographyNode>): boolean {
        return (
        nodes.length > 0 &&
        nodes.length === otherNodes.length &&
        nodes.every((node) => otherNodes.some((otherNode) => otherNode.geographyId === node.geographyId))
        );
    }
}